import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    ClipboardList, Plus, Search, Clock, Plane, Fuel, ArrowRight,
    Loader2, Timer, PlayCircle, CircleStop, ChevronDown,
    AlertTriangle, Send, PlaneLanding, PlaneTakeoff, DoorOpen, DoorClosed
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLocalToday } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeFields = [
    { key: 'actual_block_out', label: 'Block-Out', shortLabel: 'B/O', icon: PlayCircle, color: 'text-blue-400' },
    { key: 'actual_takeoff', label: 'Takeoff', shortLabel: 'T/O', icon: Plane, color: 'text-cyan-400' },
    { key: 'actual_landing', label: 'Landing', shortLabel: 'LDG', icon: ChevronDown, color: 'text-amber-400' },
    { key: 'actual_block_in', label: 'Block-In', shortLabel: 'B/I', icon: CircleStop, color: 'text-emerald-400' },
];

export default function LogbookPage() {
    const { token, hasRole, airline } = useAuth();
    const fuelUnit = airline?.fuel_unit === 'kg' ? 'kg' : 'L';
    const headers = { Authorization: `Bearer ${token}` };
    const [activeTab, setActiveTab] = useState('timing');
    const [entries, setEntries] = useState([]);
    const [flights, setFlights] = useState([]);
    const [aircraft, setAircraft] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState(getLocalToday());
    const [dialogOpen, setDialogOpen] = useState(false);
    const [fuelData, setFuelData] = useState({});
    const [reportDialog, setReportDialog] = useState(null);
    const [reportForm, setReportForm] = useState({ report_type: 'observation', severity: 'minor', title: '', description: '', ata_chapter: '', action_required: '' });
    const [flightReports, setFlightReports] = useState({});
    const [newEntry, setNewEntry] = useState({
        flight_id: '', aircraft_id: '', departure_airport: '', arrival_airport: '',
        block_off: '', block_on: '', takeoff_time: '', landing_time: '',
        fuel_departure: 0, fuel_arrival: 0, landings: 1, remarks: ''
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [eRes, fRes, aRes] = await Promise.all([
                axios.get(`${API}/logbook`, { headers }),
                axios.get(`${API}/flights?date=${dateFilter}`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/aircraft`, { headers }).catch(() => ({ data: [] }))
            ]);
            setEntries(eRes.data);
            setFlights(fRes.data);
            setAircraft(aRes.data);
        } catch { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    }, [dateFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const recordTime = async (flightId, timeField) => {
        const now = new Date().toISOString();
        const fuel = fuelData[flightId] || {};
        try {
            const res = await axios.patch(`${API}/flights/${flightId}/times`, {
                time_field: timeField,
                time_value: now,
                fuel_departure: fuel.fuel_departure || null,
                fuel_remaining: fuel.fuel_remaining || null,
                fuel_trip: fuel.fuel_trip || null,
            }, { headers });
            toast.success(res.data.message);
            if (res.data.logbook_entry) {
                toast.success('Entrada de bitacora auto-creada');
            }
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al registrar tiempo');
        }
    };

    const updateFuelField = (flightId, field, value) => {
        setFuelData(prev => ({
            ...prev,
            [flightId]: { ...(prev[flightId] || {}), [field]: parseFloat(value) || 0 }
        }));
    };

    const fetchReports = async (flightId) => {
        try {
            const res = await axios.get(`${API}/flights/${flightId}/reports`, { headers });
            setFlightReports(prev => ({ ...prev, [flightId]: res.data }));
        } catch { toast.error('No se pudieron cargar los reportes del vuelo'); }
    };

    const submitReport = async () => {
        if (!reportDialog || !reportForm.title) return;
        try {
            await axios.post(`${API}/flights/${reportDialog}/reports`, reportForm, { headers });
            toast.success('Reporte enviado. Notificaciones enviadas a Mantenimiento, Despacho y CCO.');
            setReportDialog(null);
            setReportForm({ report_type: 'observation', severity: 'minor', title: '', description: '', ata_chapter: '', action_required: '' });
            fetchReports(reportDialog);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al enviar reporte');
        }
    };

    const handleFlightSelect = (flightId) => {
        const flight = flights.find(f => f.id === flightId);
        if (flight) {
            setNewEntry(prev => ({
                ...prev, flight_id: flightId, aircraft_id: flight.aircraft_id || '',
                departure_airport: flight.origin || '', arrival_airport: flight.destination || ''
            }));
        }
    };

    const handleCreateEntry = async (e) => {
        e.preventDefault();
        const seq = [['Block-Out', newEntry.block_off], ['Despegue', newEntry.takeoff_time], ['Aterrizaje', newEntry.landing_time], ['Block-In', newEntry.block_on]];
        for (let i = 1; i < seq.length; i++) {
            if (new Date(seq[i][1]) < new Date(seq[i - 1][1])) {
                toast.error(`${seq[i][0]} no puede ser anterior a ${seq[i - 1][0]}. Si el vuelo cruza la medianoche, usa la fecha del día siguiente.`);
                return;
            }
        }
        try {
            const data = {
                ...newEntry,
                block_off: new Date(newEntry.block_off).toISOString(),
                block_on: new Date(newEntry.block_on).toISOString(),
                takeoff_time: new Date(newEntry.takeoff_time).toISOString(),
                landing_time: new Date(newEntry.landing_time).toISOString(),
            };
            await axios.post(`${API}/logbook`, data, { headers });
            toast.success('Entrada de bitacora creada');
            setDialogOpen(false);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear entrada'); }
    };

    const formatTime = (mins) => {
        if ((!mins && mins !== 0) || mins < 0) return '--:--';
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h}:${String(m).padStart(2, '0')}`;
    };

    const totalFlightTime = entries.reduce((s, e) => s + Math.max(0, e.flight_time_minutes || 0), 0);
    const totalBlockTime = entries.reduce((s, e) => s + Math.max(0, e.block_time_minutes || 0), 0);
    const canRecord = hasRole('admin') || hasRole('pilot') || hasRole('ccod') || hasRole('dispatcher');
    const canCreate = hasRole('admin') || hasRole('pilot');

    // Active flights (today, not cancelled)
    const activeFlights = flights.filter(f => f.status !== 'cancelled' && f.status !== 'arrived');

    const filteredEntries = entries.filter(e => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (e.departure_airport || '').toLowerCase().includes(s) ||
            (e.arrival_airport || '').toLowerCase().includes(s) ||
            (e.flight_number || '').toLowerCase().includes(s);
    });

    return (
        <div className="space-y-6" data-testid="logbook-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="logbook-title">
                        Bitacora
                    </h1>
                    <p className="text-muted-foreground mt-1">Registro de tiempos y entradas de vuelo</p>
                </div>
                <div className="flex items-center gap-2">
                    <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="w-[160px] input-aviation font-mono" data-testid="logbook-date" />
                    {canCreate && (
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="btn-primary" data-testid="new-logbook-btn"><Plus className="w-4 h-4 mr-2" />Manual</Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                                <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Entrada Manual</DialogTitle></DialogHeader>
                                <form onSubmit={handleCreateEntry} className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label>Vuelo</Label>
                                            <Select value={newEntry.flight_id} onValueChange={handleFlightSelect}>
                                                <SelectTrigger data-testid="logbook-flight-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                                <SelectContent>{flights.map(f => <SelectItem key={f.id} value={f.id}>{f.flight_number} {f.origin}→{f.destination}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label>Aeronave</Label>
                                            <Select value={newEntry.aircraft_id} onValueChange={v => setNewEntry({...newEntry, aircraft_id: v})}>
                                                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                                <SelectContent>{aircraft.map(a => <SelectItem key={a.id} value={a.id}>{a.registration}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5"><Label>Salida (ICAO)</Label><Input value={newEntry.departure_airport} onChange={e => setNewEntry({...newEntry, departure_airport: e.target.value.toUpperCase()})} maxLength={4} required /></div>
                                        <div className="space-y-1.5"><Label>Llegada (ICAO)</Label><Input value={newEntry.arrival_airport} onChange={e => setNewEntry({...newEntry, arrival_airport: e.target.value.toUpperCase()})} maxLength={4} required /></div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5"><Label>Block-Out</Label><Input type="datetime-local" value={newEntry.block_off} onChange={e => setNewEntry({...newEntry, block_off: e.target.value})} required data-testid="logbook-block-off" /></div>
                                        <div className="space-y-1.5"><Label>Takeoff</Label><Input type="datetime-local" value={newEntry.takeoff_time} onChange={e => setNewEntry({...newEntry, takeoff_time: e.target.value})} required /></div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5"><Label>Landing</Label><Input type="datetime-local" value={newEntry.landing_time} onChange={e => setNewEntry({...newEntry, landing_time: e.target.value})} required /></div>
                                        <div className="space-y-1.5"><Label>Block-In</Label><Input type="datetime-local" value={newEntry.block_on} onChange={e => setNewEntry({...newEntry, block_on: e.target.value})} required /></div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="space-y-1.5"><Label>Fuel Sal. ({fuelUnit})</Label><Input type="number" value={newEntry.fuel_departure} onChange={e => setNewEntry({...newEntry, fuel_departure: parseFloat(e.target.value)||0})} /></div>
                                        <div className="space-y-1.5"><Label>Fuel Lleg. ({fuelUnit})</Label><Input type="number" value={newEntry.fuel_arrival} onChange={e => setNewEntry({...newEntry, fuel_arrival: parseFloat(e.target.value)||0})} /></div>
                                        <div className="space-y-1.5"><Label>Aterrizajes</Label><Input type="number" min="1" value={newEntry.landings} onChange={e => setNewEntry({...newEntry, landings: parseInt(e.target.value)||1})} /></div>
                                    </div>
                                    <div className="space-y-1.5"><Label>Observaciones</Label><Textarea value={newEntry.remarks} onChange={e => setNewEntry({...newEntry, remarks: e.target.value})} rows={2} /></div>
                                    <Button type="submit" className="w-full btn-primary" data-testid="create-logbook-submit">Guardar</Button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="card-aviation"><CardContent className="p-3 flex items-center gap-2"><Timer className="w-5 h-5 text-primary" /><div><p className="text-xl font-bold font-mono">{formatTime(totalFlightTime)}</p><p className="text-[10px] text-muted-foreground uppercase">T. Vuelo</p></div></CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 flex items-center gap-2"><Clock className="w-5 h-5 text-blue-400" /><div><p className="text-xl font-bold font-mono">{formatTime(totalBlockTime)}</p><p className="text-[10px] text-muted-foreground uppercase">T. Block</p></div></CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 flex items-center gap-2"><Plane className="w-5 h-5 text-emerald-500" /><div><p className="text-xl font-bold font-mono">{entries.reduce((s,e)=>s+(e.landings||0),0)}</p><p className="text-[10px] text-muted-foreground uppercase">Aterrizajes</p></div></CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-500" /><div><p className="text-xl font-bold font-mono">{entries.length}</p><p className="text-[10px] text-muted-foreground uppercase">Entradas</p></div></CardContent></Card>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="timing" data-testid="tab-timing">Tiempos Reales</TabsTrigger>
                    <TabsTrigger value="logbook" data-testid="tab-logbook">Historial</TabsTrigger>
                    <TabsTrigger value="reports" data-testid="tab-reports">Reportes Cap.</TabsTrigger>
                </TabsList>

                <TabsContent value="timing" className="space-y-3 mt-4">
                    <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Vuelos Activos — {dateFilter}</h2>
                    {loading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                    ) : activeFlights.length === 0 ? (
                        <Card className="card-aviation"><CardContent className="p-8 text-center text-muted-foreground"><Plane className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No hay vuelos activos</p></CardContent></Card>
                    ) : activeFlights.map(f => (
                        <Card key={f.id} className="card-aviation" data-testid={`timing-flight-${f.id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <p className="font-bold font-mono text-lg">{f.flight_number}</p>
                                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                            <span className="font-mono">{f.origin}</span><ArrowRight className="w-3 h-3" /><span className="font-mono">{f.destination}</span>
                                        </div>
                                        {f.aircraft_registration && <Badge variant="outline" className="text-[10px] font-mono">{f.aircraft_registration}</Badge>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge className="text-xs">{f.status}</Badge>
                                        {canRecord && (
                                            <Button size="sm" variant="outline" className="h-7 text-xs text-amber-500 border-amber-500/30 hover:bg-amber-500/10" onClick={() => { setReportDialog(f.id); fetchReports(f.id); }} data-testid={`report-btn-${f.id}`}>
                                                <AlertTriangle className="w-3 h-3 mr-1" />Reporte
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                {/* Time fields */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {timeFields.map(tf => {
                                        const Icon = tf.icon;
                                        const value = f[tf.key];
                                        const recorded = !!value;
                                        return (
                                            <div key={tf.key} className={`p-2.5 rounded-lg border text-center ${recorded ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-muted/20 border-border/30'}`} data-testid={`time-${tf.key}-${f.id}`}>
                                                <Icon className={`w-4 h-4 mx-auto mb-1 ${recorded ? tf.color : 'text-muted-foreground'}`} />
                                                <p className="text-[10px] uppercase text-muted-foreground font-bold">{tf.label}</p>
                                                {recorded ? (
                                                    <p className="font-mono text-sm font-bold mt-1">{new Date(value).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</p>
                                                ) : canRecord ? (
                                                    <Button size="sm" variant="ghost" className="h-6 text-[10px] mt-1" onClick={() => recordTime(f.id, tf.key)} data-testid={`record-${tf.key}-${f.id}`}>
                                                        Registrar Ahora
                                                    </Button>
                                                ) : (
                                                    <p className="text-xs text-muted-foreground mt-1">--:--</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Fuel section */}
                                <div className="mt-3 pt-3 border-t border-border/30">
                                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2"><Fuel className="w-3 h-3 inline mr-1" />Combustible ({fuelUnit})</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="space-y-1">
                                            <Label className="text-[10px]">Salida</Label>
                                            <Input type="number" placeholder="0" className="h-8 text-sm font-mono input-aviation" value={fuelData[f.id]?.fuel_departure ?? f.fuel_departure ?? ''} onChange={e => updateFuelField(f.id, 'fuel_departure', e.target.value)} data-testid={`fuel-departure-${f.id}`} />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-[10px]">Fuel Trip</Label>
                                            <Input type="number" placeholder="0" className="h-8 text-sm font-mono input-aviation" value={fuelData[f.id]?.fuel_trip ?? f.fuel_trip ?? ''} onChange={e => updateFuelField(f.id, 'fuel_trip', e.target.value)} data-testid={`fuel-trip-${f.id}`} />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-[10px]">Remanente</Label>
                                            <Input type="number" placeholder="0" className="h-8 text-sm font-mono input-aviation" value={fuelData[f.id]?.fuel_remaining ?? f.fuel_remaining ?? ''} onChange={e => updateFuelField(f.id, 'fuel_remaining', e.target.value)} data-testid={`fuel-remaining-${f.id}`} />
                                        </div>
                                    </div>
                                </div>
                                {/* Calculated times */}
                                {f.actual_block_out && f.actual_block_in && (
                                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-border/30 text-xs text-muted-foreground">
                                        <span>Block: <strong className="text-foreground font-mono">{formatTime(Math.round((new Date(f.actual_block_in) - new Date(f.actual_block_out))/60000))}</strong></span>
                                        {f.actual_takeoff && f.actual_landing && <span>Vuelo: <strong className="text-foreground font-mono">{formatTime(Math.round((new Date(f.actual_landing) - new Date(f.actual_takeoff))/60000))}</strong></span>}
                                    </div>
                                )}
                                {/* Flight reports for this flight */}
                                {flightReports[f.id]?.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-border/30">
                                        <p className="text-[10px] font-bold uppercase text-amber-400 mb-1">Reportes ({flightReports[f.id].length})</p>
                                        {flightReports[f.id].map(r => (
                                            <div key={r.id} className={`text-xs p-1.5 rounded mb-1 ${r.severity === 'critical' ? 'bg-red-500/10 text-red-400' : r.severity === 'major' ? 'bg-amber-500/10 text-amber-400' : 'bg-blue-500/10 text-blue-400'}`}>
                                                <span className="font-bold">[{r.report_type.toUpperCase()}]</span> {r.title} — <span className="opacity-70">{r.status}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </TabsContent>

                <TabsContent value="logbook" className="space-y-3 mt-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 input-aviation" data-testid="search-logbook" />
                    </div>
                    {filteredEntries.length === 0 ? (
                        <Card className="card-aviation"><CardContent className="p-8 text-center text-muted-foreground"><ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No hay entradas</p></CardContent></Card>
                    ) : (
                        <Card className="card-aviation">
                            <CardContent className="p-0 overflow-x-auto">
                                <table className="table-aviation w-full">
                                    <thead><tr><th>Fecha</th><th>Vuelo</th><th>A/C</th><th>Ruta</th><th>T. Vuelo</th><th>T. Block</th><th>Fuel Sal.</th><th>Fuel Trip</th><th>Remanente</th><th>LDG</th></tr></thead>
                                    <tbody>
                                        {filteredEntries.map(e => {
                                            const ac = aircraft.find(a => a.id === e.aircraft_id);
                                            return (
                                                <tr key={e.id} data-testid={`logbook-row-${e.id}`}>
                                                    <td className="font-mono text-xs">{e.block_off ? new Date(e.block_off).toLocaleDateString('es') : '-'}</td>
                                                    <td className="font-mono font-bold text-primary text-sm">{e.flight_number || '-'}</td>
                                                    <td className="font-mono text-xs">{e.aircraft_registration || ac?.registration || '-'}</td>
                                                    <td className="text-xs"><span className="font-mono">{e.departure_airport}</span><span className="mx-1 text-muted-foreground">-</span><span className="font-mono">{e.arrival_airport}</span></td>
                                                    <td className="font-mono text-sm">{e.needs_review ? <Badge variant="outline" className="text-amber-600 border-amber-500/40" data-testid={`logbook-review-${e.id}`}>Revisar tiempos</Badge> : formatTime(e.flight_time_minutes)}</td>
                                                    <td className="font-mono text-sm">{formatTime(e.block_time_minutes)}</td>
                                                    <td className="font-mono text-xs">{e.fuel_departure || '-'}</td>
                                                    <td className="font-mono text-xs">{e.fuel_trip || '-'}</td>
                                                    <td className="font-mono text-xs">{e.fuel_remaining || '-'}</td>
                                                    <td className="font-mono text-center">{e.landings || 0}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Captain Reports Tab */}
                <TabsContent value="reports" className="space-y-3 mt-4">
                    <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Reportes del Capitan</h2>
                    {Object.entries(flightReports).flatMap(([fid, reports]) => reports).length === 0 ? (
                        <Card className="card-aviation"><CardContent className="p-8 text-center text-muted-foreground"><AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No hay reportes. Use el boton "Reporte" en cada vuelo activo para crear uno.</p></CardContent></Card>
                    ) : (
                        <div className="space-y-2">
                            {Object.entries(flightReports).flatMap(([fid, reports]) => reports).sort((a,b) => b.created_at?.localeCompare(a.created_at)).map(r => (
                                <Card key={r.id} className={`card-aviation border-l-4 ${r.severity === 'critical' ? 'border-l-red-500' : r.severity === 'major' ? 'border-l-amber-500' : 'border-l-blue-500'}`} data-testid={`report-${r.id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <Badge variant={r.severity === 'critical' ? 'destructive' : 'outline'} className="text-[10px]">{r.severity?.toUpperCase()}</Badge>
                                                <Badge variant="outline" className="text-[10px]">{r.report_type?.toUpperCase()}</Badge>
                                                <span className="font-mono text-sm font-bold text-primary">{r.flight_number}</span>
                                                {r.aircraft_registration && <span className="text-xs text-muted-foreground font-mono">{r.aircraft_registration}</span>}
                                            </div>
                                            <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
                                        </div>
                                        <p className="font-semibold text-sm">{r.title}</p>
                                        <p className="text-xs text-muted-foreground mt-1">{r.description}</p>
                                        {r.ata_chapter && <p className="text-[10px] mt-1 text-muted-foreground">ATA: {r.ata_chapter}</p>}
                                        {r.maintenance_notes && (
                                            <div className="mt-2 p-2 rounded bg-amber-500/10 text-xs">
                                                <p className="font-bold text-amber-400">Resp. Mantenimiento:</p>
                                                <p className="text-muted-foreground">{r.maintenance_notes}</p>
                                            </div>
                                        )}
                                        <p className="text-[10px] text-muted-foreground/60 mt-2">Por: {r.reported_by_name} - {r.created_at ? new Date(r.created_at).toLocaleString('es') : ''}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Report Dialog */}
            <Dialog open={!!reportDialog} onOpenChange={(open) => { if (!open) setReportDialog(null); }}>
                <DialogContent className="max-w-lg" data-testid="report-dialog">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Reporte del Capitan</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Tipo</Label>
                                <Select value={reportForm.report_type} onValueChange={v => setReportForm({...reportForm, report_type: v})}>
                                    <SelectTrigger data-testid="report-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="defect">Defecto</SelectItem>
                                        <SelectItem value="observation">Observacion</SelectItem>
                                        <SelectItem value="mel">MEL</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Severidad</Label>
                                <Select value={reportForm.severity} onValueChange={v => setReportForm({...reportForm, severity: v})}>
                                    <SelectTrigger data-testid="report-severity"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="critical">Critico</SelectItem>
                                        <SelectItem value="major">Mayor</SelectItem>
                                        <SelectItem value="minor">Menor</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Titulo</Label>
                            <Input value={reportForm.title} onChange={e => setReportForm({...reportForm, title: e.target.value})} placeholder="Ej: Luz de advertencia motor 1" data-testid="report-title" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Descripcion</Label>
                            <Textarea value={reportForm.description} onChange={e => setReportForm({...reportForm, description: e.target.value})} placeholder="Describe el defecto u observacion en detalle..." rows={3} data-testid="report-description" />
                        </div>
                        {/* Auditoria 6 - Item 13: ATA y Accion Requerida son campos de Mantenimiento (investigacion), no del piloto al reportar. Se llenaran al investigar. */}
                        <div className="p-2 rounded bg-amber-500/10 text-xs text-amber-300">
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            Al enviar, se notificara automaticamente a: Mantenimiento, Despachador y CCO
                        </div>
                        <Button onClick={submitReport} className="w-full btn-primary" disabled={!reportForm.title} data-testid="submit-report">
                            <Send className="w-4 h-4 mr-2" />Enviar Reporte
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
